/**
 * TrainingMonitoringConsentSettings (TCX-8, K113)
 *
 * User-accessible consent-management surface. Shows the technician's current
 * Training Companion monitoring-consent state for the active shop and lets them
 * grant, revoke, or re-consent (when the disclosure version changed). The server
 * mint/ingest gates remain the authority — this surface only reflects and edits
 * the durable server record (never localStorage).
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  getTrainingMonitoringConsentState,
  grantTrainingMonitoringConsent,
  revokeTrainingMonitoringConsent,
  CURRENT_TRAINING_MONITORING_POLICY_VERSION,
  type ConsentState,
} from '@/lib/training/trainingMonitoringConsent';
import {
  TRAINING_MONITORING_CONSENT_BOUNDARIES,
  TRAINING_MONITORING_NOT_COLLECTED,
} from '@/components/training/TrainingMonitoringConsentModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldOff, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function TrainingMonitoringConsentSettings() {
  const { user, activeShop } = useAuth();
  const [tpi, setTpi] = useState<string | null>(null);
  const [state, setState] = useState<ConsentState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!user?.id || !activeShop?.id) { setState(null); return; }
      const { data: profile } = await supabase
        .from('technician_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      const id = (profile as { id?: string } | null)?.id ?? null;
      setTpi(id);
      if (!id) { setState(null); return; }
      setState(await getTrainingMonitoringConsentState({ technicianProfileId: id, shopId: activeShop.id }));
    } catch {
      setError('Could not load your monitoring-consent status.');
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, activeShop?.id]);

  useEffect(() => { void load(); }, [load]);

  const doGrant = useCallback(async () => {
    if (!tpi || !activeShop?.id) return;
    setBusy(true);
    const res = await grantTrainingMonitoringConsent({ technicianProfileId: tpi, shopId: activeShop.id });
    setBusy(false);
    if (res.success) { toast.success('Training monitoring enabled'); void load(); }
    else toast.error(res.error ?? 'Could not save your choice');
  }, [tpi, activeShop?.id, load]);

  const doRevoke = useCallback(async () => {
    if (!tpi || !activeShop?.id) return;
    setBusy(true);
    const res = await revokeTrainingMonitoringConsent({ technicianProfileId: tpi, shopId: activeShop.id });
    setBusy(false);
    if (res.success) { toast.success('Training monitoring turned off'); void load(); }
    else toast.error(res.error ?? 'Could not revoke consent');
  }, [tpi, activeShop?.id, load]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading monitoring-consent status…
        </CardContent>
      </Card>
    );
  }

  if (!tpi) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Training monitoring</CardTitle>
          <CardDescription>
            Monitoring consent applies to technician accounts launching training from OptiBay.
            No technician profile is linked to your account for this shop.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const active = state?.isActive ?? false;
  const stale = state?.isStale ?? false;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {active ? <ShieldCheck className="h-5 w-5 text-emerald-600" />
              : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
            <CardTitle className="text-base">Training Companion monitoring</CardTitle>
          </div>
          {active ? <Badge variant="secondary">Enabled</Badge>
            : stale ? <Badge variant="outline" className="border-amber-500 text-amber-600">Update needed</Badge>
            : <Badge variant="outline">Off</Badge>}
        </div>
        <CardDescription>
          Controls whether OptiBay records limited engagement signals from the training tabs you
          launch from OptiBay. This never marks a course as verified-complete on its own, and
          declining does not falsely mark training complete.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
          </div>
        ) : null}

        {stale ? (
          <div className="flex items-start gap-2 rounded-md bg-amber-500/10 p-2 text-sm text-amber-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Our monitoring disclosure was updated. Your previous choice
              (version {state?.policyVersion}) no longer authorizes monitoring — please review and
              re-confirm to continue with the Companion.
            </span>
          </div>
        ) : null}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Status</dt>
          <dd>{active ? 'Enabled' : stale ? 'Needs re-consent' : 'Not enabled'}</dd>
          <dt className="text-muted-foreground">Accepted version</dt>
          <dd>{state?.policyVersion ?? '—'} <span className="text-muted-foreground">(current: {CURRENT_TRAINING_MONITORING_POLICY_VERSION})</span></dd>
          <dt className="text-muted-foreground">Granted</dt>
          <dd>{state?.grantedAt ? new Date(state.grantedAt).toLocaleString() : '—'}</dd>
        </dl>

        <div>
          <p className="mb-1 text-sm font-medium">What OptiBay may collect from the launched training tab</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {TRAINING_MONITORING_CONSENT_BOUNDARIES.map((l) => (
              <li key={l} className="flex gap-2">
                <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-1 text-sm font-medium">What OptiBay does not collect</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {TRAINING_MONITORING_NOT_COLLECTED.map((l) => (
              <li key={l} className="flex gap-2">
                <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-2 pt-1">
          {active ? (
            <Button variant="outline" onClick={doRevoke} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Turn off monitoring
            </Button>
          ) : (
            <Button onClick={doGrant} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {stale ? 'Review & re-enable' : 'Enable monitoring'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
